import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { MIN_SEARCH_CHARS, useEmployeeSearch } from '../../lib/useEmployees';
import { useOrg } from '../../lib/org';
import type { EmployeeSearchResultDto } from '../../lib/types';
import { SEARCH_DEBOUNCE_MS } from '../../lib/constants';
import { useDebounce } from '../../lib/useDebounce';
import { EmployeeSearchResults } from './EmployeeSearchResults';

type Props = {
  inputRef: RefObject<HTMLInputElement | null>;
};

export function EmployeeSearch({ inputRef }: Props) {
  const navigate = useNavigate();
  const org = useOrg();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = 'emp-search-listbox';

  // Server-side search on its own cache key: this bar is the only surface that must find former
  // employees, so it cannot filter the shared ['employees'] directory list. The dropdown opens on
  // the settled query, so a half-typed name never renders as "no employees found".
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const searchQuery = useEmployeeSearch(debouncedQuery);
  const matches = searchQuery.data ?? [];

  const showDropdown = open && debouncedQuery.trim().length >= MIN_SEARCH_CHARS;
  const loadingResults = searchQuery.isPending || debouncedQuery.trim() !== query.trim();

  function selectEmployee(emp: EmployeeSearchResultDto) {
    setQuery('');
    setOpen(false);
    setActiveIdx(-1);
    setMobileExpanded(false);
    inputRef.current?.blur();
    navigate(`/people/${emp.id}`);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
      setMobileExpanded(false);
      inputRef.current?.blur();
      return;
    }
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIdx >= 0 && matches[activeIdx]) {
      e.preventDefault();
      selectEmployee(matches[activeIdx]);
    }
  }

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIdx(-1);
        setMobileExpanded(false);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  // On mobile the input is hidden until expanded. Intercept the global shortcut
  // here so we can expand first, then focus.
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;
      if (['INPUT', 'TEXTAREA'].includes(tag)) return;
      const isSlash = e.key === '/';
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key === 'k';
      if (!isSlash && !isCmdK) return;
      // Check whether we're in mobile mode by comparing viewport width to the md breakpoint (768px).
      if (window.innerWidth < 768) {
        e.preventDefault();
        setMobileExpanded(true);
        // Focus the input after React commits the render of the mobile overlay.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            inputRef.current?.focus();
          });
        });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inputRef]);

  return (
    <div ref={containerRef} className="relative">
      {/* Mobile icon button — shown below md when not expanded */}
      {/* Matches the TopBar icon cluster this sits inside (slate glyph in a hit-circle), not the
          Button palette; md:hidden, so only the 44px touch height ever renders. */}
      {/* eslint-disable-next-line local/no-self-sized-control -- TopBar icon-cluster treatment; see above */}
      <button
        type="button"
        aria-label="Open search"
        onClick={() => {
          setMobileExpanded(true);
          // Focus the input on next tick after state update
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className={`flex h-11 w-11 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 md:hidden ${mobileExpanded ? 'hidden' : ''}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" />
          <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {/* Search input — always visible at md+; shown on mobile when expanded */}
      <div className={`${mobileExpanded ? 'flex' : 'hidden md:block'}`}>
        {mobileExpanded ? (
          /* Mobile expanded: full-width input in a fixed overlay row */
          <div className="fixed inset-x-0 top-0 z-50 flex h-14 items-center gap-2 border-b border-slate-200 bg-surface px-3 md:hidden">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className="shrink-0 text-slate-400"
            >
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(-1);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                if (!showDropdown) {
                  setMobileExpanded(false);
                }
              }}
              placeholder="Search employees…"
              aria-label="Search employees"
              aria-autocomplete="list"
              aria-controls={showDropdown ? listId : undefined}
              aria-activedescendant={
                showDropdown && activeIdx >= 0 ? `emp-search-opt-${activeIdx}` : undefined
              }
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={showDropdown}
              autoComplete="off"
              className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
            />
            {/* eslint-disable-next-line local/no-self-sized-control -- TopBar icon-cluster treatment, as above */}
            <button
              type="button"
              aria-label="Close search"
              onClick={() => {
                setMobileExpanded(false);
                setQuery('');
                setOpen(false);
              }}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            {showDropdown ? (
              <EmployeeSearchResults
                listId={listId}
                matches={matches}
                loading={loadingResults}
                failed={searchQuery.isError}
                activeIdx={activeIdx}
                org={org}
                className="absolute left-0 top-full z-50 max-h-80 w-full overflow-y-auto rounded-b-xl border border-slate-200 bg-surface"
                onHover={setActiveIdx}
                onSelect={selectEmployee}
              />
            ) : null}
          </div>
        ) : (
          /* Desktop input */
          <div className="relative">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            >
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(-1);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder="Search employees…"
              aria-label="Search employees"
              aria-autocomplete="list"
              aria-controls={showDropdown ? listId : undefined}
              aria-activedescendant={
                showDropdown && activeIdx >= 0 ? `emp-search-opt-${activeIdx}` : undefined
              }
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={showDropdown}
              autoComplete="off"
              className="h-8 w-60 rounded-full border border-slate-200 bg-slate-50 pl-8 pr-10 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-400 focus:bg-surface focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            {!showDropdown ? (
              <span
                aria-hidden
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-slate-200 bg-surface px-1 text-[9px] font-medium text-slate-400"
              >
                /
              </span>
            ) : null}

            {showDropdown ? (
              <EmployeeSearchResults
                listId={listId}
                matches={matches}
                loading={loadingResults}
                failed={searchQuery.isError}
                activeIdx={activeIdx}
                org={org}
                className="absolute left-0 top-[calc(100%+4px)] z-50 max-h-80 w-80 overflow-y-auto rounded-xl border border-slate-200 bg-surface"
                onHover={setActiveIdx}
                onSelect={selectEmployee}
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
